import { quizSetSchema, type QuizSet } from "@quizshow/protocol";

const databaseName = "quizshow-studio";
const quizStoreName = "quiz_sets";
const mediaStoreName = "media_blobs";

interface StoredMedia {
  mediaId: string;
  blob: Blob;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, 2);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(quizStoreName)) {
        database.createObjectStore(quizStoreName, { keyPath: "quizId" });
      }
      if (!database.objectStoreNames.contains(mediaStoreName)) {
        database.createObjectStore(mediaStoreName, { keyPath: "mediaId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Lokale Quizdatenbank konnte nicht geöffnet werden."));
  });
}

async function transaction<T>(
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(storeName, mode);
    const request = operation(tx.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Lokale Speicherung ist fehlgeschlagen."));
    tx.oncomplete = () => database.close();
  });
}

export async function listQuizzes(): Promise<QuizSet[]> {
  const quizzes = await transaction(quizStoreName, "readonly", (store) => store.getAll());
  return (quizzes as unknown[])
    .map((quiz) => quizSetSchema.safeParse(quiz))
    .filter((result) => result.success)
    .map((result) => result.data)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export async function loadQuiz(quizId: string): Promise<QuizSet | undefined> {
  const stored = await transaction(quizStoreName, "readonly", (store) => store.get(quizId));
  if (!stored) return undefined;
  const parsed = quizSetSchema.safeParse(stored);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Das lokal gespeicherte Quiz ist ungültig.");
  }
  return parsed.data;
}

export async function saveQuiz(quiz: QuizSet): Promise<void> {
  const parsed = quizSetSchema.safeParse(quiz);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Das Quiz konnte nicht gespeichert werden.");
  }
  await transaction(quizStoreName, "readwrite", (store) => store.put(parsed.data));
}

export async function deleteQuiz(quizId: string): Promise<void> {
  const quiz = await loadQuiz(quizId);
  if (quiz) {
    await Promise.all(quiz.mediaAssets.map((asset) => deleteMedia(asset.mediaId)));
  }
  await transaction(quizStoreName, "readwrite", (store) => store.delete(quizId));
}

export async function saveMedia(mediaId: string, blob: Blob): Promise<void> {
  await transaction(mediaStoreName, "readwrite", (store) => store.put({ mediaId, blob } satisfies StoredMedia));
}

export async function loadMedia(mediaId: string): Promise<Blob | undefined> {
  const stored = (await transaction(mediaStoreName, "readonly", (store) => store.get(mediaId))) as StoredMedia | undefined;
  return stored?.blob;
}

export async function deleteMedia(mediaId: string): Promise<void> {
  await transaction(mediaStoreName, "readwrite", (store) => store.delete(mediaId));
}
